'use client'

import { useState } from 'react'
import { ChevronDown } from 'lucide-react'

interface FilterBarProps {
  onFilterChange?: (filters: FilterState) => void
}

interface FilterState {
  industryTag: string
  carrierTag: string
}

// 与数据库 work_case_industries.industry_tag 编码一致（-1 表示全部）
const INDUSTRY_TAGS = [
  { value: '-1', label: '全部' },
  { value: '609', label: '人工智能' },
  { value: '620', label: '大数据' },
  { value: '630', label: '能源' },
  { value: '601', label: '电商' },
  { value: '602', label: '企业内部管理' },
  { value: '613', label: '物联网' },
  { value: '608', label: '医疗健康' },
  { value: '605', label: '金融' },
  { value: '616', label: '内容平台' },
  { value: '607', label: '云计算' },
  { value: '628', label: '工业互联网' },
  { value: '612', label: '企业服务(SaaS)' },
  { value: '631', label: '零售/新消费' },
  { value: '626', label: '汽车' },
  { value: '603', label: '社交' },
  { value: '604', label: '在线教育' },
  { value: '606', label: '旅游' },
  { value: '610', label: '物流仓储' },
  { value: '611', label: '外卖跑腿' },
  { value: '614', label: 'VR/AR' },
  { value: '615', label: '区块链' },
  { value: '617', label: '出行' },
  { value: '618', label: '安全' },
  { value: '619', label: '人力资源/HR' },
  { value: '621', label: '广告营销' },
  { value: '622', label: '农业' },
  { value: '623', label: '搜索' },
  { value: '624', label: '音视频' },
  { value: '625', label: '生活服务' },
  { value: '627', label: '游戏/电竞' },
  { value: '629', label: '智慧数字孪生' },
  { value: '632', label: '政务服务' },
  { value: '633', label: '房地产' }
]

// 与数据库 work_case_carriers.carrier_tag 编码一致（-1 表示全部）
const CARRIER_TAGS = [
  { value: '-1', label: '全部' },
  { value: '501', label: '安卓APP' },
  { value: '502', label: 'IOS APP' },
  { value: '503', label: '网站' },
  { value: '504', label: '小程序' },
  { value: '505', label: 'H5' },
  { value: '506', label: '爬虫/脚本' },
  { value: '507', label: '插件' },
  { value: '508', label: '游戏' },
  { value: '509', label: 'Windows应用' },
  { value: '510', label: 'Mac应用' },
  { value: '511', label: '嵌入式软件' },
  { value: '512', label: '硬件' },
  { value: '514', label: '云服务/云平台' },
  { value: '515', label: '算法模型' },
  { value: '516', label: '框架或代码包' },
  { value: '517', label: '车载应用' }
]

export function FilterBar({ onFilterChange }: FilterBarProps) {
  const [industryOpen, setIndustryOpen] = useState(false)
  const [carrierOpen, setCarrierOpen] = useState(false)
  const [selectedIndustry, setSelectedIndustry] = useState('-1')
  const [selectedCarrier, setSelectedCarrier] = useState('-1')

  const handleIndustrySelect = (value: string) => {
    setSelectedIndustry(value)
    setIndustryOpen(false)
    onFilterChange?.({ industryTag: value, carrierTag: selectedCarrier })
  }

  const handleCarrierSelect = (value: string) => {
    setSelectedCarrier(value)
    setCarrierOpen(false)
    onFilterChange?.({ industryTag: selectedIndustry, carrierTag: value })
  }

  const handleReset = () => {
    setSelectedIndustry('-1')
    setSelectedCarrier('-1')
    onFilterChange?.({ industryTag: '-1', carrierTag: '-1' })
  }

  const selectedIndustryLabel =
    INDUSTRY_TAGS.find(t => t.value === selectedIndustry)?.label || '全部'
  const selectedCarrierLabel =
    CARRIER_TAGS.find(t => t.value === selectedCarrier)?.label || '全部'

  return (
    <div className="flex flex-wrap items-center gap-4">
      {/* Industry Tag Filter */}
      <div className="relative">
        <button
          onClick={() => setIndustryOpen(!industryOpen)}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          aria-haspopup="listbox"
          aria-expanded={industryOpen}
        >
          <span className="text-sm text-gray-700">行业标签</span>
          <span className="text-sm text-gray-500">: {selectedIndustryLabel}</span>
          <ChevronDown className="w-4 h-4 text-gray-400" />
        </button>

        {industryOpen && (
          <div className="absolute z-100 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-96 overflow-y-auto">
            {INDUSTRY_TAGS.map(tag => (
              <button
                key={tag.value}
                onClick={() => handleIndustrySelect(tag.value)}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors ${
                  selectedIndustry === tag.value ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                }`}
              >
                {tag.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Carrier Tag Filter */}
      <div className="relative">
        <button
          onClick={() => setCarrierOpen(!carrierOpen)}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          aria-haspopup="listbox"
          aria-expanded={carrierOpen}
        >
          <span className="text-sm text-gray-700">平台载体</span>
          <span className="text-sm text-gray-500">: {selectedCarrierLabel}</span>
          <ChevronDown className="w-4 h-4 text-gray-400" />
        </button>

        {carrierOpen && (
          <div className="absolute z-100 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-96 overflow-y-auto">
            {CARRIER_TAGS.map(tag => (
              <button
                key={tag.value}
                onClick={() => handleCarrierSelect(tag.value)}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors ${
                  selectedCarrier === tag.value ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                }`}
              >
                {tag.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Reset Button */}
      {(selectedIndustry !== '-1' || selectedCarrier !== '-1') && (
        <button
          onClick={handleReset}
          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          重置筛选
        </button>
      )}
    </div>
  )
}
